import React, { useState } from "react";

import { Pagination } from "@performyard/py-components";
import type { SortChangedEvent } from "@performyard/py-components";
import { ASYNC_TASK_NAMES } from "@performyard/service-contracts";
import type {
	CompetencyAssignmentJSON,
	CompetencyAssignmentUserDict,
	CompetencyDict,
} from "@performyard/service-contracts";

import DownloadButtonSSE from "../../../../../components.smart/DownloadButtonSSE";
import FilterChips from "../../../../../components/FilterChips";
import PageHeading from "../../../../../components/PageHeading";
import { SideNavigationLink } from "../../../../../components/SideNavigation";
import CompetenciesFilterPanel from "../../../components/CompetenciesFilterPanel";
import { type CompetenciesFilter } from "../../../components/CompetenciesFilterPanel/schema";
import {
	convertFiltersToFilterChips,
	getActiveFiltersCount,
	getNonDismissibleChipIds,
	removeFilter,
} from "../../../components/CompetenciesFilterPanel/utils";
import CompetenciesTable from "../../../components/CompetenciesTable";
import UpdateCurrentLevelModal from "../../../components/UpdateCurrentLevelModal";
import ViewHistoryPanel from "../../../components/ViewHistoryPanel";
import ViewLevelsPanel from "../../../components/ViewLevelsPanel";

import { useCompanyCompetencyAssignments } from "../../../../../api/hooks";
import { useIsNewNavigation } from "../../../../../api/hooks/defs/navigation";
import { useUserAsyncTask } from "../../../../../hooks/useUserAsyncTask";
import useCompetencyAssignmentActions from "../../../hooks/useCompetencyAssignmentActions";

import { requestCompetencyAssignmentsCsv } from "../../../../../api/services";
import type { ListCompanyCompetencyAssignmentsParams } from "../../../../../api/services";

import { PAGE_SIZE, colIdToSortBy, competencySortFromEvent } from "../../../utils";

import InfoPageTemplate from "../../../../../pageTemplates/InfoPageTemplate";
import StandardToolbarActionRow from "../../../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";

const PAGE_TITLE = "Company Competencies";

type AllCompetenciesProps = {
	navLinks: SideNavigationLink[];
	canUpdateCurrentLevel: boolean;
	canExportCsv: boolean;
};

const AllCompetencies = ({
	navLinks,
	canUpdateCurrentLevel,
	canExportCsv,
}: AllCompetenciesProps): React.ReactElement => {
	const {
		pageNumber,
		setPageNumber,
		sort,
		setSort,
		filters,
		setFilters,
		searchValue,
		debouncedSearchValue,
		setDebouncedSearch,
	} = useInfoPageTemplateQueryState<CompetenciesFilter>({
		defaultSortName: "competency_name",
		defaultSortDirection: "asc",
		defaultPageNumber: 1,
	});

	const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);
	const isNewNav = useIsNewNavigation();

	const params: ListCompanyCompetencyAssignmentsParams = {
		sortBy: colIdToSortBy(sort.name) ?? "competency_name",
		sortOrder: sort.direction,
		status: filters.status,
		source: filters.source,
		levelGap: filters.levelGap,
		assigneeId: filters.assignee?.id,
		competencyIds: filters.competencies?.map((competency) => competency.id),
		roleIds: filters.roles?.map((role) => role.id),
		search: debouncedSearchValue || undefined,
		limit: PAGE_SIZE,
		skip: (pageNumber - 1) * PAGE_SIZE,
	};

	const { data, isLoading } = useCompanyCompetencyAssignments(params, {
		placeholderData: (previousData) => previousData,
	});

	const { task: exportTask, triggerTask: triggerExport } = useUserAsyncTask(
		ASYNC_TASK_NAMES.EXPORT_COMPETENCY_ASSIGNMENTS_CSV
	);

	const assignments: CompetencyAssignmentJSON[] = data?.assignments ?? [];
	const competencies: CompetencyDict = data?.competencies ?? {};
	const users: CompetencyAssignmentUserDict = data?.users ?? {};
	const totalCount = data?.totalCount ?? 0;
	const pageCount = Math.ceil(totalCount / PAGE_SIZE);

	// Dropdown options come from the ACTIVE-ONLY competencyOptions/roleOptions fields, not the
	// competencies resolution dict above (which may include archived entries referenced by
	// returned assignments).
	const competencyOptions = Object.entries(data?.competencyOptions ?? {})
		.map(([competencyId, competency]) => ({ _id: competencyId, name: competency.name }))
		.sort((optionA, optionB) => optionA.name.localeCompare(optionB.name));

	const roleOptions = Object.entries(data?.roleOptions ?? {})
		.map(([roleId, role]) => ({ _id: roleId, name: role.name }))
		.sort((optionA, optionB) => optionA.name.localeCompare(optionB.name));

	const {
		openPanel,
		actionSelectedRow,
		isUpdateModalOpen,
		onOpenPanel,
		closePanel,
		onUpdateLevel,
		closeUpdateModal,
	} = useCompetencyAssignmentActions();

	const activeCompetency = actionSelectedRow ? competencies[actionSelectedRow.competency_id] : undefined;
	const activeAssignee = actionSelectedRow
		? { _id: actionSelectedRow.user_id, name: users[actionSelectedRow.user_id]?.name ?? "" }
		: undefined;

	const onSortChanged = (event: SortChangedEvent<CompetencyAssignmentJSON>) => {
		const nextSort = competencySortFromEvent(event);
		if (nextSort) setSort(nextSort);
	};

	const mainContent = (
		<div data-testid="all-competencies">
			<div className="flex">
				<CompetenciesTable
					assignments={assignments}
					competencies={competencies}
					users={users}
					loading={isLoading}
					canUpdateCurrentLevel={canUpdateCurrentLevel}
					sort={sort}
					onSortChanged={onSortChanged}
					onOpenPanel={onOpenPanel}
					onUpdateLevel={onUpdateLevel}
				/>
			</div>
			{pageCount > 1 && (
				<div className="mx-auto mt-5 flex w-full max-w-[400px] justify-center">
					<Pagination currentPage={pageNumber} pageCount={pageCount} onPageChange={setPageNumber} />
				</div>
			)}
		</div>
	);

	const filterChips = convertFiltersToFilterChips(filters);

	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchValue}
			onSearchInputChange={(value) => setDebouncedSearch(value)}
			searchPlaceholder="Search competencies..."
			onFilterButtonPress={() => setIsFiltersPanelOpen(true)}
			activeFiltersCount={getActiveFiltersCount(filters)}
		/>
	);

	const drawer = (
		<CompetenciesFilterPanel
			isOpen={isFiltersPanelOpen}
			onClose={() => setIsFiltersPanelOpen(false)}
			filters={filters}
			variant="company"
			competencyOptions={competencyOptions}
			roleOptions={roleOptions}
			onApplyFilters={(appliedFilters) => setFilters(appliedFilters)}
		/>
	);

	const handleDeleteFilter = (id: string) => {
		const nextFilters = removeFilter(filters, id);
		if (nextFilters) setFilters(nextFilters);
	};

	const toolbarInformationRow = (
		<FilterChips
			filters={filterChips}
			label="Filters applied: "
			nonDismissibleFilters={getNonDismissibleChipIds(filters)}
			onDeleteFilter={handleDeleteFilter}
		/>
	);

	const actionButtons = canExportCsv
		? [
				<DownloadButtonSSE
					key="download-csv"
					task={exportTask}
					onClick={() =>
						triggerExport(() =>
							requestCompetencyAssignmentsCsv({
								scope: "company",
								search: debouncedSearchValue || undefined,
								status: filters.status,
								source: filters.source,
								levelGap: filters.levelGap,
								assigneeId: filters.assignee?.id,
								competencyIds: filters.competencies?.map((competency) => competency.id),
								roleIds: filters.roles?.map((role) => role.id),
								sortBy: colIdToSortBy(sort.name) ?? "competency_name",
								sortOrder: sort.direction,
							})
						)
					}
					testid="download-csv"
				>
					Download CSV
				</DownloadButtonSSE>,
			]
		: [];

	const assignmentId = actionSelectedRow?._id ?? "";
	return (
		<>
			<InfoPageTemplate
				pageTitle={PAGE_TITLE}
				pageHeading={isNewNav ? <PageHeading>{PAGE_TITLE}</PageHeading> : undefined}
				navigationLinks={navLinks}
				activeNavigationLinkKey="all"
				actionButtons={actionButtons}
				toolbarActionRow={toolbarActionRow}
				drawer={drawer}
				toolbarInformationRow={toolbarInformationRow}
				mainContent={mainContent}
			/>
			<ViewLevelsPanel
				key={`levels-${assignmentId}`}
				isOpen={openPanel === "levels"}
				onClose={closePanel}
				competency={activeCompetency}
				assignee={activeAssignee}
				currentLevel={actionSelectedRow?.current_level ?? null}
				expectedLevel={actionSelectedRow?.expected_level ?? null}
			/>
			<ViewHistoryPanel
				key={`history-${assignmentId}`}
				isOpen={openPanel === "history"}
				onClose={closePanel}
				assignmentId={assignmentId}
				competency={activeCompetency}
				assignee={activeAssignee}
			/>
			<UpdateCurrentLevelModal
				isOpen={isUpdateModalOpen}
				onClose={closeUpdateModal}
				assignment={actionSelectedRow}
				competency={activeCompetency}
				assignee={activeAssignee}
			/>
		</>
	);
};

export default AllCompetencies;
